// pages/roster-management/bulk-upload/Upload.tsx
import React, { useState } from 'react';
import type Locale from '@whizz/api-client/dist/@types/model/Locale';
import type { RosterManagementStudentListItem, TagsGet } from '@whizz/api-model';
import { Translate, useI18n } from '@whizz/react-i18n';
import exportFromJson from 'export-from-json';
import { Button, MaterialIcon } from '../../../../components';
import RosterManagementLayout from '../../../../layouts/RosterManagementLayout';
import { useSuccessErrorModal } from '../../../../hooks/useSuccessErrorModal';
import { GLOBAL_ICONS, genders } from '../../../../util/vars';
import { useCleverIntegration } from '../../../../hooks/useCleverIntegration';
import { repository } from '@whizz/api-client';
import { RosterManagementStudentActionEnum } from '@whizz/api-model';
import { DateTime } from 'luxon';
import { range, sortBy } from 'lodash';
import StudentUploadPage from './StudentUploadPage';
import whizzyIcon from '../../../../assets/icon-default-whizzy.svg';
import studentsIcon from '../../../../assets/Cluedo-Nudges-Mexican-Wave.svg';
import techersIcon from '../../../../assets/Cluedo-Nudges-Teachers-Admin.svg';
import parentsIcon from '../../../../assets/Cluedo-Nudges-Teachers-Magnify.svg';
import styled, { createGlobalStyle } from 'styled-components';
import Modal, { ModalFooter } from '../../../../components/modal/Modal';
import { useIdentity } from '@whizz/react-identity';
import type Teacher from '@whizz/api-client/dist/@types/model/Teacher';


type Panel = 'students' | 'teachers' | 'parents' | null;


const BulkUploadOverrides = createGlobalStyle`
  /* Only affect the bulk-upload page */
  .bulk-upload-page .bg-white.rounded-default.shadow {
    background: transparent !important;
    box-shadow: none !important;
  }
`;

const csvCell = (value: string | number | null | undefined) => {
  const text = String(value ?? '');
  return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

const csvLine = (values: Array<string | number | null | undefined>) =>
  values.map(csvCell).join(',');

const cleanCsvHeader = (value: string) =>
  String(value ?? '')
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/g, ' ')
    .trim();

const JOB_TYPE_LABEL_BY_ID: Record<number, string> = {
  0: 'Teacher',
  1: 'Principal/Head teacher',
  2: 'Deputy/Assistant Principal/Headteacher',
  3: 'Maths Lead',
  4: 'Senior Leadership team member',
  5: 'Teaching Assistant',
  6: 'Finance',
  7: 'Other',
};


const Guidelines = styled('div')`
  th,
  td {
    text-align: left;
    border: 1px solid #b7b7b7;
    padding: 0.2rem;
    font-size: 0.75rem;

    @media (min-width: 1024px) {
      padding: 0.3rem;
      font-size: 1rem;
    }
  }
  li {
    & span {
      font-weight: bold;
    }
  }
`;

const Section = styled.div`
  width: 100%;
  border: 1px solid #e6e6e6;
  background: #fff;
  border-radius: 10px;
  overflow: hidden;
`;

const SectionHeader = styled.button<{ $open: boolean }>`
  width: 100%;
  text-align: left;
  padding: 18px 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: ${(p) => (p.$open ? '#f9fafb' : '#fff')};
  cursor: pointer;
  border: 0;
`;

const Title = styled.div`
  display: flex;
  gap: 12px;
  align-items: baseline;
  font-size: 16px;

  .pill {
    color: #67b500; /* "positive" green */
    font-weight: 800;
    letter-spacing: 0.3px;
  }
  .sub {
    color: #555;
    font-weight: 500;
  }
`;

const SectionBody = styled.div`
  padding: 18px 20px 22px 20px;
  border-top: 1px solid #eee;
`;

const FullBleed = styled.div`
  position: relative;
  padding: 0px 50px 0px 50px;
  left: 50%;
  right: 50%;
  margin-left: -50vw;
  margin-right: -50vw;
  width: 100vw;
`;

const TranslateWithSpan = ({
  i18nKey,
  className,
}: {
  i18nKey: string;
  className?: string;
}) => (
  <Translate i18nKey={i18nKey}>
    {[ <span key="span" className={className} /> ]}
  </Translate>
);

const TranslateWithBoldSpan = ({ i18nKey }: { i18nKey: string }) => (
  <Translate i18nKey={i18nKey}>
    {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
    {/* @ts-ignore */}
    <span className="font-bold" />
  </Translate>
);

const getActiveLocaleId = (
  locale: Locale | null,
  me: unknown,
  fallback = 'en-gb',
) => (
  locale?.getId() ??
  (me as { locale?: { id?: string | null } })?.locale?.id ??
  fallback
).toLowerCase();

const Upload2 = (): JSX.Element => {
  const { locale, translate } = useI18n();
  const [ successErrorModal, toggleMessageType ] = useSuccessErrorModal();
  const [ isClever ] = useCleverIntegration();
  const [ openPanel, setOpenPanel ] = useState<Panel>(null); // default
  const [ showStudentUploader, setShowStudentUploader ] = useState(false);
  const [ showTeacherUploader, setShowTeacherUploader ] = useState(false);
  const [ showParentUploader, setShowParentUploader ] = useState(false);
  const [ studentUploaderReady, setStudentUploaderReady ] = useState(false);
  const [ teacherUploaderReady, setTeacherUploaderReady ] = useState(false);
  const [ parentUploaderReady, setParentUploaderReady ] = useState(false);

  const [ studentsDirty, setStudentsDirty ] = useState(false);
  const [ teachersDirty, setTeachersDirty ] = useState(false);
  const [ parentsDirty, setParentsDirty ] = useState(false);

  // === Confirm modal state & helpers (INSIDE component) ===
  const [ confirmOpen, setConfirmOpen ] = React.useState(false);
  const [ confirmTitle, setConfirmTitle ] = React.useState<string>('');
  const [ confirmBody, setConfirmBody ] = React.useState<React.ReactNode>(null);
  const [ confirmDestructive, setConfirmDestructive ] = React.useState(false);
  const [ confirmOkText, setConfirmOkText ] = React.useState('OK');
  const [ confirmCancelText, setConfirmCancelText ] = React.useState('Cancel');
  const confirmResolveRef = React.useRef<null | ((v: boolean) => void)>(null);

  //bulk uploads
  const { me } = useIdentity();
  const localeId = getActiveLocaleId(locale, me);
  const userCountry = (me as Teacher).country;
  const canAddOrEditStaff = userCountry ? ![ 'nz' ].includes(userCountry) : false;
  const studentsRef = React.useRef<HTMLDivElement>(null);
  const teachersRef = React.useRef<HTMLDivElement>(null);
  const parentsRef  = React.useRef<HTMLDivElement>(null);
  const studentExampleRowsV2 = [
    {
      archive: translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.EXPORT_EXAMPLES.STUDENTS.EXAMPLE1.ARCHIVE'),
      upn: translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.EXPORT_EXAMPLES.STUDENTS.EXAMPLE1.UPN'),
      firstName: translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.EXPORT_EXAMPLES.STUDENTS.EXAMPLE1.FIRST_NAME'),
      lastName: translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.EXPORT_EXAMPLES.STUDENTS.EXAMPLE1.LAST_NAME'),
      dob: translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.EXPORT_EXAMPLES.STUDENTS.EXAMPLE1.DOB'),
      gender: translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.EXPORT_EXAMPLES.STUDENTS.EXAMPLE1.GENDER'),
      username: translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.EXPORT_EXAMPLES.STUDENTS.EXAMPLE1.USERNAME'),
      password: translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.EXPORT_EXAMPLES.STUDENTS.EXAMPLE1.PASSWORD'),
      license: translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.EXPORT_EXAMPLES.STUDENTS.EXAMPLE1.LICENSE'),
      yearGroup: translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.EXPORT_EXAMPLES.STUDENTS.EXAMPLE1.YEAR_GROUP'),
      className: translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.EXPORT_EXAMPLES.STUDENTS.EXAMPLE1.CLASS'),
      tag: translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.EXPORT_EXAMPLES.STUDENTS.EXAMPLE1.TAG'),
      assessmentDelta: translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.EXPORT_EXAMPLES.STUDENTS.EXAMPLE1.ASSESSMENT_DELTA'),
    },
    {
      archive: translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.EXPORT_EXAMPLES.STUDENTS.EXAMPLE2.ARCHIVE'),
      upn: translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.EXPORT_EXAMPLES.STUDENTS.EXAMPLE2.UPN'),
      firstName: translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.EXPORT_EXAMPLES.STUDENTS.EXAMPLE2.FIRST_NAME'),
      lastName: translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.EXPORT_EXAMPLES.STUDENTS.EXAMPLE2.LAST_NAME'),
      dob: translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.EXPORT_EXAMPLES.STUDENTS.EXAMPLE2.DOB'),
      yearGroup: translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.EXPORT_EXAMPLES.STUDENTS.EXAMPLE2.YEAR_GROUP'),
      gender: translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.EXPORT_EXAMPLES.STUDENTS.EXAMPLE2.GENDER'),
      password: translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.EXPORT_EXAMPLES.STUDENTS.EXAMPLE2.PASSWORD'),
      username: translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.EXPORT_EXAMPLES.STUDENTS.EXAMPLE2.USERNAME'),
      license: translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.EXPORT_EXAMPLES.STUDENTS.EXAMPLE2.LICENSE'),
      className: translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.EXPORT_EXAMPLES.STUDENTS.EXAMPLE2.CLASS'),
      tag: translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.EXPORT_EXAMPLES.STUDENTS.EXAMPLE2.TAG'),
      assessmentDelta: translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.EXPORT_EXAMPLES.STUDENTS.EXAMPLE2.ASSESSMENT_DELTA'),
    },
  ];
  const teacherExampleRowsv2 = [
    {
      jobtype: translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.EXPORT_EXAMPLES.TEACHERS.EXAMPLE1.JOBTYPE'),
      firstName: translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.EXPORT_EXAMPLES.TEACHERS.EXAMPLE1.FIRSTNAME'),
      lastName: translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.EXPORT_EXAMPLES.TEACHERS.EXAMPLE1.LASTNAME'),
      email: translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.EXPORT_EXAMPLES.TEACHERS.EXAMPLE1.EMAIL'),
      recoveryEmail: translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.EXPORT_EXAMPLES.TEACHERS.EXAMPLE1.RECOVERYEMAIL'),
      phone: translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.EXPORT_EXAMPLES.TEACHERS.EXAMPLE1.PHONE'),
      classes: translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.EXPORT_EXAMPLES.TEACHERS.EXAMPLE1.CLASSES'),
      classEmails: translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.EXPORT_EXAMPLES.TEACHERS.EXAMPLE1.CLASSEMAILS'),
      admin: translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.EXPORT_EXAMPLES.TEACHERS.EXAMPLE1.ADMIN'),
    },
    {
      jobtype: translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.EXPORT_EXAMPLES.TEACHERS.EXAMPLE2.JOBTYPE'),
      firstName: translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.EXPORT_EXAMPLES.TEACHERS.EXAMPLE2.FIRSTNAME'),
      lastName: translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.EXPORT_EXAMPLES.TEACHERS.EXAMPLE2.LASTNAME'),
      email: translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.EXPORT_EXAMPLES.TEACHERS.EXAMPLE2.EMAIL'),
      recoveryEmail: translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.EXPORT_EXAMPLES.TEACHERS.EXAMPLE2.RECOVERYEMAIL'),
      phone: translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.EXPORT_EXAMPLES.TEACHERS.EXAMPLE2.PHONE'),
      classes: translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.EXPORT_EXAMPLES.TEACHERS.EXAMPLE2.CLASSES'),
      classEmails: translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.EXPORT_EXAMPLES.TEACHERS.EXAMPLE2.CLASSEMAILS'),
      admin: translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.EXPORT_EXAMPLES.TEACHERS.EXAMPLE2.ADMIN'),
    },
  ];
  const parentExampleRowsV2 = [
    {
      upn: translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.EXPORT_EXAMPLES.PARENTS.EXAMPLE1.UPN'),
      studentName: translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.EXPORT_EXAMPLES.PARENTS.EXAMPLE1.STUDENT_NAME'),
      g1Name: translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.EXPORT_EXAMPLES.PARENTS.EXAMPLE1.G1_NAME'),
      e1: translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.EXPORT_EXAMPLES.PARENTS.EXAMPLE1.G1_EMAIL'),
      g2Name: translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.EXPORT_EXAMPLES.PARENTS.EXAMPLE1.G2_NAME'),
      e2: translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.EXPORT_EXAMPLES.PARENTS.EXAMPLE1.G2_EMAIL'),
      g3Name: translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.EXPORT_EXAMPLES.PARENTS.EXAMPLE1.G3_NAME'),
      e3: translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.EXPORT_EXAMPLES.PARENTS.EXAMPLE1.G3_EMAIL'),
    },
    {
      upn: translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.EXPORT_EXAMPLES.PARENTS.EXAMPLE2.UPN'),
      studentName: translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.EXPORT_EXAMPLES.PARENTS.EXAMPLE2.STUDENT_NAME'),
      g1Name: translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.EXPORT_EXAMPLES.PARENTS.EXAMPLE2.G1_NAME'),
      e1: translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.EXPORT_EXAMPLES.PARENTS.EXAMPLE2.G1_EMAIL'),
      g2Name: translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.EXPORT_EXAMPLES.PARENTS.EXAMPLE2.G2_NAME'),
      e2: translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.EXPORT_EXAMPLES.PARENTS.EXAMPLE2.G2_EMAIL'),
      g3Name: translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.EXPORT_EXAMPLES.PARENTS.EXAMPLE2.G3_NAME'),
      e3: translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.EXPORT_EXAMPLES.PARENTS.EXAMPLE2.G3_EMAIL'),
    },
  ];

  const TOP_OFFSET = 12;
  const STICKY_OFFSET = 12;

  const STUDENT_TEMPLATE_HEADERS = [
    cleanCsvHeader(translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.STUDENTS.ARCHIVE')),
    cleanCsvHeader(translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.STUDENTS.UPN')),
    cleanCsvHeader(translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.STUDENTS.FIRST_NAME')),
    cleanCsvHeader(translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.STUDENTS.LAST_NAME')),
    cleanCsvHeader(translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.STUDENTS.DOB')),
    cleanCsvHeader(translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.STUDENTS.GENDER')),
    cleanCsvHeader(translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.STUDENTS.USERNAME')),
    cleanCsvHeader(translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.STUDENTS.PASSWORD')),
    cleanCsvHeader(translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.STUDENTS.LICENSE')),
    cleanCsvHeader(translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.STUDENTS.YEAR_GROUP')),
    cleanCsvHeader(translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.STUDENTS.CLASS')),
    cleanCsvHeader(translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.STUDENTS.TAG')),
    cleanCsvHeader(translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.STUDENTS.ASSESSMENT_DELTA')),
  ];

  const TEACHER_TEMPLATE_HEADERS = [
    translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.TEACHERS.JOB_TYPE'),
    translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.TEACHERS.FIRST_NAME'),
    translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.TEACHERS.LAST_NAME'),
    translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.TEACHERS.EMAIL'),
    translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.TEACHERS.RECOVERY_EMAIL'),
    translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.TEACHERS.PHONE'),
    translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.TEACHERS.CLASSES'),
    translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.TEACHERS.EMAIL_CLASSES'),
    translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.TEACHERS.ADMIN'),
  ];


  const PARENT_TEMPLATE_HEADERS = [
    translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.PARENTS.UPN'),
    translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.PARENTS.STUDENT_NAME'),
    translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.PARENTS.G1_NAME'),
    translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.PARENTS.G1_EMAIL'),
    translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.PARENTS.G2_NAME'),
    translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.PARENTS.G2_EMAIL'),
    translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.PARENTS.G3_NAME'),
    translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.PARENTS.G3_EMAIL'),
  ];

  function downloadBlankParentsCsv(translate: (key: string) => string) {
    const BOM = '\ufeff';
    const exampleRow = [
      translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.BLANK_TEMPLATE_EXAMPLE.PARENTS.UPN'),
      translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.BLANK_TEMPLATE_EXAMPLE.PARENTS.STUDENT_NAME'),
      translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.BLANK_TEMPLATE_EXAMPLE.PARENTS.G1_NAME'),
      translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.BLANK_TEMPLATE_EXAMPLE.PARENTS.G1_EMAIL'),
      translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.BLANK_TEMPLATE_EXAMPLE.PARENTS.G2_NAME'),
      translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.BLANK_TEMPLATE_EXAMPLE.PARENTS.G2_EMAIL'),
      translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.BLANK_TEMPLATE_EXAMPLE.PARENTS.G3_NAME'),
      translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.BLANK_TEMPLATE_EXAMPLE.PARENTS.G3_EMAIL'),
    ];
    const csv = `${csvLine(PARENT_TEMPLATE_HEADERS)}\r\n${csvLine(exampleRow)}\r\n`;
    const blob = new Blob([ BOM + csv ], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;

    const fileName =
      translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.COMMON.EXPORT_BLANK_PARENTS_FILENAME') ||
      'Maths_Whizz_Parents_Blank_Template.csv';

    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  // --- Blank Student CSV template (headers match StudentUploadPage importer) ---
  function downloadBlankStudentCsv(translate: (key: string) => string, localeId: string) {
    const BOM = '\ufeff';
    const exampleYearGroup = localeId === 'en-us' || localeId === 'en-ke' ? 'G2' : 'Y2';
    const exampleRow = [
      translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.BLANK_TEMPLATE_EXAMPLE.STUDENTS.ARCHIVE'),
      translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.BLANK_TEMPLATE_EXAMPLE.STUDENTS.UPN'),
      translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.BLANK_TEMPLATE_EXAMPLE.STUDENTS.FIRST_NAME'),
      translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.BLANK_TEMPLATE_EXAMPLE.STUDENTS.LAST_NAME'),
      translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.BLANK_TEMPLATE_EXAMPLE.STUDENTS.DOB'),
      translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.BLANK_TEMPLATE_EXAMPLE.STUDENTS.GENDER'),
      translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.BLANK_TEMPLATE_EXAMPLE.STUDENTS.USERNAME'),
      translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.BLANK_TEMPLATE_EXAMPLE.STUDENTS.PASSWORD'),
      translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.BLANK_TEMPLATE_EXAMPLE.STUDENTS.LICENSE'),
      exampleYearGroup,
      translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.BLANK_TEMPLATE_EXAMPLE.STUDENTS.CLASS'),
      translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.BLANK_TEMPLATE_EXAMPLE.STUDENTS.TAG'),
      translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.BLANK_TEMPLATE_EXAMPLE.STUDENTS.ASSESSMENT_DELTA'),
    ];
    const csv = `${csvLine(STUDENT_TEMPLATE_HEADERS)}\r\n${csvLine(exampleRow)}\r\n`;
    const blob = new Blob([ BOM + csv ], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;

    // Localised filename with fallback
    const fileName =
      translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.COMMON.EXPORT_BLANK_STUDENTS_FILENAME') ||
      'Maths_Whizz_Student_Blank_Template.csv';

    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  function downloadBlankTeacherCsv(translate: (key: string) => string) {
    const BOM = '\ufeff';
    const exampleRow = [
      translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.BLANK_TEMPLATE_EXAMPLE.TEACHERS.JOB_TYPE'),
      translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.BLANK_TEMPLATE_EXAMPLE.TEACHERS.FIRST_NAME'),
      translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.BLANK_TEMPLATE_EXAMPLE.TEACHERS.LAST_NAME'),
      translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.BLANK_TEMPLATE_EXAMPLE.TEACHERS.EMAIL'),
      translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.BLANK_TEMPLATE_EXAMPLE.TEACHERS.RECOVERY_EMAIL'),
      translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.BLANK_TEMPLATE_EXAMPLE.TEACHERS.PHONE'),
      translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.BLANK_TEMPLATE_EXAMPLE.TEACHERS.CLASSES'),
      translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.BLANK_TEMPLATE_EXAMPLE.TEACHERS.EMAIL_CLASSES'),
      translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.BLANK_TEMPLATE_EXAMPLE.TEACHERS.ADMIN'),
    ];
    const csv = `${csvLine(TEACHER_TEMPLATE_HEADERS)}\r\n${csvLine(exampleRow)}\r\n`;
    const blob = new Blob([ BOM + csv ], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;

    const fileName =
      translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.COMMON.EXPORT_BLANK_TEACHERS_FILENAME') ||
      'Maths_Whizz_Staff_Blank_Template.csv';

    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  const studentExamples = [
    {
      firstName: translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.STUDENTS.FIRST_NAME'),
      lastName: translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.STUDENTS.LAST_NAME'),
      upn: <TranslateWithSpan i18nKey={'pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.STUDENTS.UPN'} />,
      yearGroup: translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.STUDENTS.YEAR_GROUP'),
      gender: translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.STUDENTS.GENDER'),
      password: translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.STUDENTS.PASSWORD'),
      activate: translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.STUDENTS.UPN'),
      dob: translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.STUDENTS.DOB'),
      classes: [ 'EXAMPLE1.CLASS' ],
      tags: [ 'SEN' ],
    },
    {
      firstName: 'EXAMPLE2.FIRST_NAME',
      lastName: 'EXAMPLE2.LAST_NAME',
      upn: 'EXAMPLE2.UPN',
      yearGroup: 'EXAMPLE2.YEAR_GROUP',
      gender: 'EXAMPLE2.GENDER',
      password: 'EXAMPLE2.PASSWORD',
      activate: 'EXAMPLE2.ACTIVATE',
      dob: 'EXAMPLE2.DOB',
      classes: [ 'EXAMPLE2.CLASS' ],
      tags: [ '' ],
    },
  ];

  React.useEffect(() => {
    if (!openPanel) return;

    // 1 frame to render expand, then a microtask for any layout shifts
    const raf = requestAnimationFrame(() => {
      setTimeout(() => {
        scrollToPanel(openPanel);
      }, 0);
    });

    return () => cancelAnimationFrame(raf);
  }, [ openPanel, showStudentUploader, showTeacherUploader, showParentUploader ]);


  const scrollToPanel = (panel: Panel) => {
    if (!panel) return;

    const map = { students: studentsRef, teachers: teachersRef, parents: parentsRef } as const;
    const el = map[panel]?.current;
    if (!el) return;

    const doScroll = () => {
      const rectTop = el.getBoundingClientRect().top;
      const absoluteTop = window.scrollY + rectTop;
      const targetY = Math.max(absoluteTop - STICKY_OFFSET, 0);
      window.scrollTo({ top: targetY, behavior: 'smooth' });
    };

    // run a few times to catch height shifts after expand/render
    requestAnimationFrame(() => {
      doScroll();
      setTimeout(doScroll, 60);
      setTimeout(doScroll, 180);
    });
  };


  const askConfirm = React.useCallback(
    (
      body: React.ReactNode,
      opts?: { title?: string; destructive?: boolean; confirmText?: string; cancelText?: string },
    ): Promise<boolean> => {
      setConfirmBody(body);
      setConfirmTitle(opts?.title ?? '');
      setConfirmDestructive(!!opts?.destructive);
      setConfirmOkText(opts?.confirmText ?? translate('pages.components.BUTTONS.OK'));
      setConfirmCancelText(opts?.cancelText ?? translate('pages.components.BUTTONS.CANCEL'));
      setConfirmOpen(true);
      return new Promise<boolean>((resolve) => {
        confirmResolveRef.current = resolve;
      });
    },
    [],
  );

  const answerConfirm = React.useCallback((val: boolean) => {
    setConfirmOpen(false);
    confirmResolveRef.current?.(val);
    confirmResolveRef.current = null;
  }, []);

  // Convenience for the specific “lose progress?” prompt
  const confirmLoseProgress = React.useCallback(
    () =>
      askConfirm(
        <>{translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.ALERTS.LEAVING_PAGE_WARNING')}</>,
        { title: translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.ALERTS.LEAVING_PAGE_Q'), destructive: true, confirmText: translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.ALERTS.LEAVING_PAGE_LEAVE'), cancelText: translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.ALERTS.LEAVING_PAGE_STAY') },
      ),
    [ askConfirm ],
  );


  const handleTogglePanel = async (panel: Panel) => {
    const closingCurrent = openPanel === panel;

    const leavingDirty =
      (openPanel === 'students' && showStudentUploader && studentsDirty) ||
      (openPanel === 'teachers' && showTeacherUploader && teachersDirty) ||
      (openPanel === 'parents' && showParentUploader && parentsDirty);

    if (closingCurrent) {
      if (leavingDirty) {
        const ok = await confirmLoseProgress();
        if (!ok) return;
      }
      setOpenPanel(null);
      return;
    }

    if (leavingDirty) {
      const ok = await confirmLoseProgress();
      if (!ok) return;
    }

    setOpenPanel(panel);

  };

  return (
    <div className="bulk-upload-page">
      <BulkUploadOverrides />
      <RosterManagementLayout >

        <FullBleed>
          <div className="mx-auto w-full max-w-screen-2xl px-4">
            {successErrorModal}
          </div>

          {/* Top notice */}
          <Section className="mb-4">
            <div className="px-6 py-4 md:px-8 md:py-6 flex justify-center">
              <div className="flex flex-col items-center gap-4 md:flex-row md:items-center md:gap-12">
                <img src={whizzyIcon} alt="Whizzy" className="w-28 h-auto md:w-36 flex-shrink-0 ml-3" />
                <div className="text-left leading-normal space-y-2">
                  <p className="m-0">
                    <TranslateWithSpan i18nKey={'pages.ROSTER_MANAGEMENT.BULK_UPLOADS.GUIDELINES.BLOB_TITLE'} className="font-bold" />
                  </p>
                  <ul className="m-0 pl-12 list-none space-y-1">
                    <li>- {translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.GUIDELINES.BLOB_POINT_1')}</li>
                    <li>- {translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.GUIDELINES.BLOB_POINT_2')}</li>
                    <li>- {translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.GUIDELINES.BLOB_POINT_3')}</li>
                  </ul>
                  <p className="m-0">{translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.GUIDELINES.BLOB_FOOTER')}</p>
                </div>
              </div>
            </div>
          </Section>

          {/* STUDENTS lozenge */}
          <FullBleed >
            <Section className="mb-4" ref={studentsRef} >
              <SectionHeader
                $open={openPanel === 'students'}
                onClick={() => handleTogglePanel('students')}
                aria-expanded={openPanel === 'students'}
              >
                <Title >
                  <span className="pill">{translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.LOZANGES.STUDENTS')}</span>
                  <span className="sub">{translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.LOZANGES.STUDENTS_HINT')}</span>
                  <img src={studentsIcon} alt="Whizzy" className="w-28 h-auto md:w-28" />
                </Title>
                <MaterialIcon icon={GLOBAL_ICONS[openPanel === 'students' ? 'toggle_on' : 'toggle_off']} />
              </SectionHeader>

              {openPanel === 'students' && (
                <SectionBody>
                  {!showStudentUploader ? (
                    // ───── Guidelines (shown first) ─────
                    <div className="w-full border-2 border-info rounded-default">
                      <Guidelines className="m-md space-y-12">
                        {/* Step 1 */}
                        <BulletSection
                          number={1}
                          title={translate('pages.ROSTER_MANAGEMENT.UPLOAD.GUIDELINES.SECTIONS.DOWNLOAD')}
                        >
                          <div className="space-y-2">
                            <div className="flex items-center space-x-2">

                              <Button
                                className="w-48"
                                color="positive"
                                isOutline
                                icon={GLOBAL_ICONS.download}
                                disabled={isClever}
                                onClick={() => downloadBlankStudentCsv(translate, localeId)}
                              >
                                {translate('pages.ROSTER_MANAGEMENT.UPLOAD.BUTTONS.BLANK')}
                              </Button>

                              <p>{translate('pages.ROSTER_MANAGEMENT.UPLOAD.BUTTONS.BLANK_HINT')}</p>
                            </div>
                            <div className="flex items-center space-x-2">
                              <ExportStudents />
                              <p>{translate('pages.ROSTER_MANAGEMENT.UPLOAD.BUTTONS.EXISTING_HINT')}</p>
                            </div>
                          </div>
                        </BulletSection>

                        {/* Step 2 — keep your existing table block here */}
                        <BulletSection
                          number={2}
                          title={translate('pages.ROSTER_MANAGEMENT.UPLOAD.GUIDELINES.SECTIONS.UPDATE')}
                          fullWidth
                        >
                          <div className="space-y-6">
                            <ul className="space-y-2 list-disc pl-10">
                              <li>
                                <TranslateWithSpan i18nKey={'pages.ROSTER_MANAGEMENT.UPLOAD.GUIDELINES.UPN_WITH_HINT'} />
                              </li>
                              <li>
                                <TranslateWithSpan i18nKey={'pages.ROSTER_MANAGEMENT.UPLOAD.GUIDELINES.PASSWORD_WITH_HINT'} />
                              </li>
                              <li>
                                <TranslateWithSpan i18nKey={'pages.ROSTER_MANAGEMENT.UPLOAD.GUIDELINES.ASSESSMENT_DELTA_HINT'} />
                              </li>
                              <li>
                                <TranslateWithSpan i18nKey={'pages.ROSTER_MANAGEMENT.UPLOAD.GUIDELINES.TAGS_WITH_HINT'} />
                              </li>
                            </ul>

                            <div className="w-full relative overflow-x-auto px-10">
                              <table className="w-full">
                                <thead>
                                  <tr>
                                    <th><TranslateWithSpan i18nKey={'pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.STUDENTS.ARCHIVE'} /></th>
                                    <th><TranslateWithSpan i18nKey={'pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.STUDENTS.UPN'}/></th>
                                    <th><TranslateWithSpan i18nKey={'pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.STUDENTS.FIRST_NAME'}/></th>
                                    <th><TranslateWithSpan i18nKey={'pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.STUDENTS.LAST_NAME'}/></th>
                                    <th><TranslateWithSpan i18nKey={'pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.STUDENTS.DOB'}/></th>
                                    <th><TranslateWithSpan i18nKey={'pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.STUDENTS.GENDER'}/></th>
                                    <th><TranslateWithSpan i18nKey={'pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.STUDENTS.USERNAME'}/></th>
                                    <th><TranslateWithSpan i18nKey={'pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.STUDENTS.PASSWORD'}/></th>
                                    <th><TranslateWithSpan i18nKey={'pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.STUDENTS.LICENSE'}/></th>
                                    <th><TranslateWithSpan i18nKey={'pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.STUDENTS.YEAR_GROUP'}/></th>
                                    <th><TranslateWithSpan i18nKey={'pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.STUDENTS.CLASS'}/></th>
                                    <th><TranslateWithSpan i18nKey={'pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.STUDENTS.TAG'}/></th>
                                    <th><TranslateWithSpan i18nKey={'pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.STUDENTS.ASSESSMENT_DELTA'}/></th>

                                  </tr>
                                </thead>
                                <tbody>
                                  {studentExampleRowsV2.map((r) => (
                                    <tr key={r.upn}>
                                      <td>{r.archive}</td>
                                      <td>{r.upn}</td>
                                      <td>{r.firstName}</td>
                                      <td>{r.lastName}</td>
                                      <td>{r.dob}</td>
                                      <td>{r.gender}</td>
                                      <td>{r.username}</td>
                                      <td>{r.password}</td>
                                      <td>{r.license}</td>
                                      <td>{r.yearGroup}</td>
                                      <td>{r.className}</td>
                                      <td>{r.tag}</td>
                                      <td>{r.assessmentDelta}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </BulletSection>

                        {/* Step 3 */}
                        <BulletSection
                          number={3}
                          title={translate('pages.ROSTER_MANAGEMENT.UPLOAD.GUIDELINES.SECTIONS.UPLOAD')}
                        >
                          <Button
                            className="w-48"
                            color="positive"
                            isOutline
                            icon={GLOBAL_ICONS.upload}
                            onClick={() => {
                              setStudentUploaderReady(false);
                              setShowStudentUploader(true);
                            }}
                          >
                            {translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.COMMON.DATA_PROCESSING_BUTTON')}
                          </Button>
                        </BulletSection>

                        {/* Step 4 — keep your existing block here */}
                      </Guidelines>
                    </div>
                  ) : (
                    // ───── Uploader replaces guidelines ─────
                    <>
                      <div className="mb-4">

                        <Button
                          isOutline
                          icon={GLOBAL_ICONS.back}
                          onClick={async () => {
                            if (showStudentUploader && studentsDirty) {
                              const ok = await confirmLoseProgress();
                              if (!ok) return;
                            }
                            setStudentUploaderReady(false);
                            setShowStudentUploader(false);
                          }}
                        >
                          {translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.COMMON.BACK_TO_INSTRUCTIONS')}
                        </Button>
                      </div>

                      <div className={studentUploaderReady ? '' : 'opacity-0 pointer-events-none'}>
                        <StudentUploadPage
                          onDirtyChange={setStudentsDirty}
                          autoOpenFilePicker={!studentUploaderReady}
                          onAutoOpenFilePicker={() => setStudentUploaderReady(true)}
                        />
                      </div>
                    </>
                  )}
                </SectionBody>


              )}
            </Section>
          </FullBleed>

          {/* TEACHERS lozenge */}
          {canAddOrEditStaff && (
            <FullBleed>
              <Section className="mb-4" ref={teachersRef}>
                <SectionHeader
                  $open={openPanel === 'teachers'}
                  onClick={() => handleTogglePanel('teachers')}
                  aria-expanded={openPanel === 'teachers'}
                >
                  <Title>
                    <span className="pill">{translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.LOZANGES.TEACHERS')}</span>
                    <span className="sub">{translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.LOZANGES.TEACHERS_HINT')}</span>
                    <img src={techersIcon} alt="Whizzy" className="w-28 h-auto md:w-28" />
                  </Title>
                  <MaterialIcon icon={GLOBAL_ICONS[openPanel === 'teachers' ? 'toggle_on' : 'toggle_off']} />
                </SectionHeader>

                {openPanel === 'teachers' && (
                  <SectionBody>
                    {!showTeacherUploader ? (
                      // ── Guidelines (like Students) ──
                      <div className="w-full border-2 border-info rounded-default">
                        <Guidelines className="m-md space-y-12">
                          {/* Step 1 */}
                          <BulletSection
                            number={1}
                            title={translate('pages.ROSTER_MANAGEMENT.UPLOAD.GUIDELINES.SECTIONS.DOWNLOAD')}
                          >
                            <div className="space-y-2">
                              <div className="flex items-center space-x-2">
                                <Button
                                  className="w-48"
                                  color="positive"
                                  isOutline
                                  icon={GLOBAL_ICONS.download}
                                  disabled={isClever}
                                  onClick={() => downloadBlankTeacherCsv(translate)}
                                >
                                  {translate('pages.ROSTER_MANAGEMENT.UPLOAD.BUTTONS.BLANK')}
                                </Button>
                                <p>{translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.LOZANGES.TEACHERS_TEMPLATE_BUTTON_HINT')}</p>
                              </div>
                            </div>
                            <div className="flex items-center space-x-2 mt-2">
                              <ExportTeachers />
                              <p>{translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.LOZANGES.TEACHERS_EXISTING_TEMPLATE_BUTTON_HINT')}</p>
                            </div>
                          </BulletSection>


                          {/* Step 2 */}
                          <BulletSection number={2} title={translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.LOZANGES.TEACHERS_STEP2')} fullWidth>
                            <div className="space-y-6">
                              <ul className="space-y-2 list-disc pl-10">
                                <li><TranslateWithSpan i18nKey={'pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TEACHERS.GUIDE1'} /></li>
                                <li><TranslateWithSpan i18nKey={'pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TEACHERS.GUIDE2'} /></li>
                                <li><TranslateWithSpan i18nKey={'pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TEACHERS.GUIDE3'} /></li>
                                <li><TranslateWithSpan i18nKey={'pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TEACHERS.GUIDE4'} /></li>

                              </ul>

                              <div className="w-full relative overflow-x-auto px-10">
                                <table className="w-full">
                                  <thead>
                                    <tr>
                                      <th><TranslateWithSpan i18nKey={'pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.TEACHERS.JOB_TYPE'} />*</th>
                                      <th><TranslateWithSpan i18nKey={'pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.TEACHERS.FIRST_NAME'} />*</th>
                                      <th><TranslateWithSpan i18nKey={'pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.TEACHERS.LAST_NAME'} />*</th>
                                      <th><TranslateWithSpan i18nKey={'pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.TEACHERS.EMAIL'} /><sup>*1</sup></th>
                                      <th><TranslateWithSpan i18nKey={'pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.TEACHERS.RECOVERY_EMAIL'} /><sup>*1</sup></th>
                                      <th><TranslateWithSpan i18nKey={'pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.TEACHERS.PHONE'} /></th>
                                      <th><TranslateWithSpan i18nKey={'pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.TEACHERS.CLASSES'} /></th>
                                      <th><TranslateWithSpan i18nKey={'pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.TEACHERS.EMAIL_CLASSES'} /></th>
                                      <th><TranslateWithSpan i18nKey={'pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.TEACHERS.ADMIN'} /></th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {teacherExampleRowsv2.map((r, i) => (
                                      <tr key={i}>
                                        <td>{r.jobtype}</td>
                                        <td>{r.firstName}</td>
                                        <td>{r.lastName}</td>
                                        <td>{r.email}</td>
                                        <td>{r.recoveryEmail}</td>
                                        <td>{r.phone}</td>
                                        <td>{r.classes}</td>
                                        <td>{r.classEmails}</td>
                                        <td>{r.admin}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                                <p className="mt-3">
                                  <sup className="font-bold align-super text-[0.75em]">*1</sup>{' '}
                                  <TranslateWithBoldSpan i18nKey={'pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TEACHERS.EMAIL_FOOTNOTE_BODY'} />
                                </p>
                              </div>
                            </div>
                          </BulletSection>


                          {/* Step 3 */}
                          <BulletSection number={3} title={translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.LOZANGES.UPLOAD')}>
                            <Button
                              className="w-48"
                              color="positive"
                              isOutline
                              icon={GLOBAL_ICONS.upload}
                              onClick={() => {
                                setTeacherUploaderReady(false);
                                setShowTeacherUploader(true);
                              }}
                            >
                              {translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.COMMON.DATA_PROCESSING_BUTTON')}
                            </Button>
                          </BulletSection>

                        </Guidelines>
                      </div>
                    ) : (
                      // ── Embedded uploader (Teachers tab locked) ──
                      <>
                        <div className="mb-4">
                          <Button
                            isOutline
                            icon={GLOBAL_ICONS.back}
                            onClick={async () => {
                              if (showTeacherUploader && teachersDirty) {
                                const ok = await confirmLoseProgress();
                                if (!ok) return;
                              }
                              setTeacherUploaderReady(false);
                              setShowTeacherUploader(false);
                            }}
                          >
                            {translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.COMMON.BACK_TO_INSTRUCTIONS')}
                          </Button>
                        </div>


                        {/* Reuse your uploader with Teachers tab active */}
                        <div className={teacherUploaderReady ? '' : 'opacity-0 pointer-events-none'}>
                          <StudentUploadPage
                            initialTab="teachers"
                            lockTab
                            onDirtyChange={setTeachersDirty}
                            autoOpenFilePicker={!teacherUploaderReady}
                            onAutoOpenFilePicker={() => setTeacherUploaderReady(true)}
                          />
                        </div>
                      </>
                    )}
                  </SectionBody>
                )}
              </Section>
            </FullBleed>
          )}

          {/* PARENTS lozenge */}
          <FullBleed>
            <Section ref={parentsRef}>
              <SectionHeader
                $open={openPanel === 'parents'}
                onClick={() => handleTogglePanel('parents')}
                aria-expanded={openPanel === 'parents'}
              >
                <Title>
                  <span className="pill">{translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.LOZANGES.PARENTS')}</span>
                  <span className="sub">{translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.LOZANGES.PARENTS_HINT')}</span>
                  <img src={parentsIcon} alt="Whizzy" className="w-10 h-auto md:w-10" />
                </Title>
                <MaterialIcon icon={GLOBAL_ICONS[openPanel === 'parents' ? 'toggle_on' : 'toggle_off']} />
              </SectionHeader>

              {openPanel === 'parents' && (
                <SectionBody>
                  {!showParentUploader ? (
                    // ───── Parents guidelines ─────
                    <div className="w-full border-2 border-info rounded-default">
                      <Guidelines className="m-md space-y-12">
                        <BulletSection number={1} title={translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.PARENTS.STEP1')}>
                          <div className="space-y-2">
                            <div className="flex items-center space-x-2">
                              <Button
                                className="w-48"
                                color="positive"
                                isOutline
                                icon={GLOBAL_ICONS.download}
                                onClick={() => downloadBlankParentsCsv(translate)}
                              >
                                {translate('pages.ROSTER_MANAGEMENT.UPLOAD.BUTTONS.BLANK')}
                              </Button>
                              <p>{translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.GUIDELINES.PARENTS_TEMPLATE_BUTTON_HINT')}</p>
                            </div>
                            <div className="flex items-center space-x-2 mt-2">
                              <ExportParents />
                              <p>{translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.GUIDELINES.PARENTS_EXISTING_TEMPLATE_BUTTON_HINT')}</p>
                            </div>
                          </div>
                        </BulletSection>

                        <BulletSection number={2} title={translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.PARENTS.STEP2')} fullWidth>
                          <div className="space-y-6">
                            <ul className="space-y-2 list-disc pl-10">
                              <li><TranslateWithSpan i18nKey={'pages.ROSTER_MANAGEMENT.BULK_UPLOADS.PARENTS.GUIDE1'} /></li>
                              <li><TranslateWithSpan i18nKey={'pages.ROSTER_MANAGEMENT.BULK_UPLOADS.PARENTS.GUIDE2'} /></li>
                              <li><TranslateWithSpan i18nKey={'pages.ROSTER_MANAGEMENT.BULK_UPLOADS.PARENTS.GUIDE3'} /></li>
                            </ul>

                            <div className="w-full relative overflow-x-auto px-10">
                              <table className="w-full">
                                <thead>
                                  <tr>
                                    <th><TranslateWithSpan i18nKey={'pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.PARENTS.UPN'} /></th>
                                    <th><TranslateWithSpan i18nKey={'pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.PARENTS.STUDENT_NAME'} /></th>
                                    <th><TranslateWithSpan i18nKey={'pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.PARENTS.G1_NAME'} /></th>
                                    <th><TranslateWithSpan i18nKey={'pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.PARENTS.G1_EMAIL'} /></th>
                                    <th><TranslateWithSpan i18nKey={'pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.PARENTS.G2_NAME'} /></th>
                                    <th><TranslateWithSpan i18nKey={'pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.PARENTS.G2_EMAIL'} /> </th>
                                    <th><TranslateWithSpan i18nKey={'pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.PARENTS.G3_NAME'} /></th>
                                    <th><TranslateWithSpan i18nKey={'pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.PARENTS.G3_EMAIL'} /> </th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {parentExampleRowsV2.map((r, i) => (
                                    <tr key={i}>
                                      <td>{r.upn}</td>
                                      <td>{r.studentName}</td>
                                      <td>{r.g1Name}</td>
                                      <td>{r.e1}</td>
                                      <td>{r.g2Name}</td>
                                      <td>{r.e2}</td>
                                      <td>{r.g3Name}</td>
                                      <td>{r.e3}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </BulletSection>


                        <BulletSection number={3} title={translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.LOZANGES.UPLOAD')}>
                          <Button
                            className="w-48"
                            color="positive"
                            isOutline
                            icon={GLOBAL_ICONS.upload}
                            onClick={() => {
                              setParentUploaderReady(false);
                              setShowParentUploader(true);
                            }}
                          >
                            {translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.COMMON.DATA_PROCESSING_BUTTON')}
                          </Button>
                        </BulletSection>

                      </Guidelines>
                    </div>
                  ) : (
                    // ───── Embedded uploader (replaces guidelines) ─────
                    <>
                      <div className="mb-4">
                        <Button
                          isOutline
                          icon={GLOBAL_ICONS.back}
                          onClick={async () => {
                            if (showParentUploader && parentsDirty) {
                              const ok = await confirmLoseProgress();
                              if (!ok) return;
                            }
                            setParentUploaderReady(false);
                            setShowParentUploader(false);
                          }}
                        >
                          {translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.COMMON.BACK_TO_INSTRUCTIONS')}
                        </Button>
                      </div>

                      {/* Lock to the Parents tab */}
                      <div className={parentUploaderReady ? '' : 'opacity-0 pointer-events-none'}>
                        <StudentUploadPage
                          initialTab="parents"
                          lockTab
                          onDirtyChange={setParentsDirty}
                          autoOpenFilePicker={!parentUploaderReady}
                          onAutoOpenFilePicker={() => setParentUploaderReady(true)}
                        />
                      </div>
                    </>
                  )}
                </SectionBody>
              )}

            </Section>
          </FullBleed>

        </FullBleed>

        {confirmOpen && (
          <Modal
            open
            toggle={(v?: boolean) => setConfirmOpen(Boolean(v))}
            onClose={() => answerConfirm(false)}
            disableMaskClick
            alwaysOnTop
            size="md"
          >
            <div className="p-4">
              {confirmTitle && <h3 className="text-lg font-semibold mb-2">{confirmTitle}</h3>}
              <div className="mb-4">{confirmBody}</div>
              <ModalFooter justify="end">
                {/* Stay button → green outline (like Add Row) */}
                <Button
                  data-id="whizz_button"
                  color="positive"
                  isOutline
                  className="!h-8 !py-0 !px-3 mr-2"
                  onClick={() => answerConfirm(false)}
                >
                  {confirmCancelText}
                </Button>

                {/* Leave button → red outline */}
                <Button
                  data-id="whizz_button"
                  color="negative"
                  isOutline
                  className="!h-8 !py-0 !px-3"
                  onClick={() => answerConfirm(true)}
                >
                  {confirmOkText}
                </Button>
              </ModalFooter>
            </div>
          </Modal>
        )}

      </RosterManagementLayout>
    </div>
  );
};

const BulletSection = ({
  number,
  title,
  fullWidth = false,
  children,
}: {
  number: number;
  title: string;
  children: React.ReactNode;
  fullWidth?: boolean;
}) => (
  <div className={'grid items-center'} style={{ gridTemplateColumns: '60px auto' }}>
    <h2 className={'bg-positive rounded-full p-md text-xl mr-md w-12 h-12 flex justify-center items-center font-bold'}>
      {number}
    </h2>
    <h2 className={'text-2xl font-bold text-positive'}>{title}</h2>
    <div className={`mt-md ${fullWidth ? 'col-span-2' : 'col-start-2'}`}>{children}</div>
  </div>
);

// CSV export helpers

const ExportStudents = () => {
  const { locale, translate } = useI18n();
  const [ isClever ] = useCleverIntegration();
  const [ isDownloading, setIsDownloading ] = React.useState(false);
  const { me } = useIdentity();
  const localeId = getActiveLocaleId(locale, me);
  const isMonthFirstDobLocale = localeId === 'en-us' || localeId === 'en-nz';


  const download = async () => {
    if (isClever || isDownloading) return;
    setIsDownloading(true);
    try {
      // 1) Fetch students
      const students = await repository.StudentRepository.rosterManagementListItemsByAction(
        RosterManagementStudentActionEnum.edit,
      );

      // 2) Work out schoolId for year groups (try students first, then classes)
      const schoolId: number | undefined =
        (students?.[0] as any)?.schoolId ??
        (await (async () => {
          try {
            const cls = await repository.NewClassRepository.getClasses();
            return cls?.[0]?.schoolId;
          } catch {
            return undefined;
          }
        })());

      // 3) Fetch year groups and build a map (id -> code)
      let ygById = new Map<number, string>();
      if (schoolId) {
        try {
          const yearGroups = await repository.YearGroupRepository.allBySchoolId(schoolId);
          ygById = new Map(
            yearGroups.map((yg: any) => [ (yg.yearGroupId ?? yg.id), String(yg.code ?? '').trim() ]),
          );
        } catch {
          // ignore — fall back to blank codes below
        }
      }

      const getStudentClassNames = (student: RosterManagementStudentListItem) => Array.from(
        new Set(
          (student.classes ?? [])
            .map((c: any) => String(c?.name ?? '').trim())
            .filter(Boolean),
        ),
      ).sort((a, b) => a.localeCompare(b));

      // 4) Build CSV rows — group by class, then alphabetise within each class.
      // If a student belongs to multiple classes, their full class list becomes the group key.
      const rows = sortBy(students, [
        (student) => getStudentClassNames(student).join(', ').toLowerCase(),
        (student) => String(student.lastName ?? '').trim().toLowerCase(),
        (student) => String(student.firstName ?? '').trim().toLowerCase(),
      ]).map((v: RosterManagementStudentListItem) => {

        const dob = v.dob
          ? DateTime.fromISO(v.dob).toFormat(
            isMonthFirstDobLocale ? 'LL/dd/yyyy' : 'dd/LL/yyyy',
          )
          : '';
        const ygCode = v.yearGroupId ? (ygById.get(v.yearGroupId) ?? '') : '';

        const gAbbr = (genders?.[v.gender]?.abbreviation ?? '').toUpperCase();
        const gender = gAbbr.charAt(0);

        const username = (v as any).username ?? (v as any).loginName ?? '';
        const classNames = getStudentClassNames(v).join(', ');

        const tags = Array.from(
          new Set(
            (v.tags ?? [])
              .map((t: any) => String(t?.abbreviation ?? '').trim())
              .filter(Boolean),
          ),
        ).join(',');


        return {
          [translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.STUDENTS.ARCHIVE')]: 'N',
          [translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.STUDENTS.UPN')]: v.upn ?? '',
          [translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.STUDENTS.FIRST_NAME')]: v.firstName ?? '',
          [translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.STUDENTS.LAST_NAME')]: v.lastName ?? '',
          [translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.STUDENTS.DOB')]: dob,
          [translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.STUDENTS.GENDER')]: gender,
          [translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.STUDENTS.USERNAME')]: username,
          [translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.STUDENTS.PASSWORD')]: '',
          [translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.STUDENTS.LICENSE')]: v.active ? 'Y' : '',
          [translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.STUDENTS.YEAR_GROUP')]: ygCode,
          [translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.STUDENTS.CLASS')]: classNames,
          [translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.STUDENTS.TAG')]: tags,
          [translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.STUDENTS.ASSESSMENT_DELTA')]: '',
        };
      });

      const getStudentExportHeaders = (t: (k: string) => string) => ([
        t('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.STUDENTS.ARCHIVE'),
        t('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.STUDENTS.UPN'),
        t('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.STUDENTS.FIRST_NAME'),
        t('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.STUDENTS.LAST_NAME'),
        t('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.STUDENTS.DOB'),
        t('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.STUDENTS.GENDER'),
        t('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.STUDENTS.USERNAME'),
        t('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.STUDENTS.PASSWORD'),
        t('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.STUDENTS.LICENSE'),
        t('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.STUDENTS.YEAR_GROUP'),
        t('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.STUDENTS.CLASS'),
        t('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.STUDENTS.TAG'),
        t('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.STUDENTS.ASSESSMENT_DELTA'),
      ]);

      // 5) Export
      exportFromJson({
        data: rows,
        fileName: translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.COMMON.EXPORT_STUDENTS_FILENAME') || 'Student_export',
        exportType: 'csv',
        withBOM: true,
        fields: getStudentExportHeaders(translate),
      });
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <Button
      className="w-48"
      color="positive"
      isOutline
      icon={GLOBAL_ICONS.download}
      disabled={isClever || isDownloading}
      onClick={download}
    >
      {translate(
        `pages.ROSTER_MANAGEMENT.UPLOAD.BUTTONS.${isDownloading ? 'DOWNLOADING' : 'EXISTING'}`,
      )}
    </Button>
  );
};

const ExportTeachers = () => {
  const { translate } = useI18n();
  const [ isDownloading, setIsDownloading ] = React.useState(false);
  const jobTypeLookups = React.useMemo(() => {
    const canonJobType = (value: string) => value.trim().toLowerCase().replace(/[\s/-]+/g, '');
    const jobTypes = [
      { id: 0, label: 'Teacher', key: 'pages.ROSTER_MANAGEMENT.STAFF.JOB_TITLES.TEACHER', aliases: [ 'teacher' ] },
      { id: 1, label: 'Principal/Head teacher', key: 'pages.ROSTER_MANAGEMENT.STAFF.JOB_TITLES.PRINCIPAL', aliases: [ 'principal', 'head', 'headteacher' ] },
      { id: 2, label: 'Deputy/Assistant Principal/Headteacher', key: 'pages.ROSTER_MANAGEMENT.STAFF.JOB_TITLES.DEPUTY', aliases: [ 'deputy', 'assistantprincipal', 'assistantheadteacher' ] },
      { id: 3, label: 'Maths Lead', key: 'pages.ROSTER_MANAGEMENT.STAFF.JOB_TITLES.MATHS_LEAD', aliases: [ 'mathslead', 'mathlead', 'mathematicslead' ] },
      { id: 4, label: 'Senior Leadership team member', key: 'pages.ROSTER_MANAGEMENT.STAFF.JOB_TITLES.SENIOR_LEADERSHIP', aliases: [ 'slt', 'seniorleadership', 'seniorleadershipteammember' ] },
      { id: 5, label: 'Teaching Assistant', key: 'pages.ROSTER_MANAGEMENT.STAFF.JOB_TITLES.ASSISTANT', aliases: [ 'ta', 'teachingassistant' ] },
      { id: 6, label: 'Finance', key: 'pages.ROSTER_MANAGEMENT.STAFF.JOB_TITLES.FINANCE', aliases: [ 'finance' ] },
      { id: 7, label: 'Other', key: 'pages.ROSTER_MANAGEMENT.STAFF.JOB_TITLES.OTHER', aliases: [ 'other' ] },
    ] as const;

    const labelById = Object.fromEntries(jobTypes.map((jobType) => [
      jobType.id,
      String(translate(jobType.key, jobType.label)),
    ])) as Record<number, string>;

    const idByCanon: Record<string, number> = {};
    jobTypes.forEach((jobType) => {
      [ jobType.label, labelById[jobType.id], ...jobType.aliases ].forEach((candidate) => {
        idByCanon[canonJobType(String(candidate))] = jobType.id;
      });
    });

    return { labelById, idByCanon, canonJobType };
  }, [ translate ]);
  const localizeJobTypeText = React.useCallback((raw?: string | number | null) => {
    const text = String(raw ?? '').trim();
    if (!text) return jobTypeLookups.labelById[0] ?? 'Teacher';

    if (/^\d+$/.test(text)) {
      const numeric = Number(text);
      if (Number.isInteger(numeric) && numeric >= 0 && numeric <= 7) {
        return jobTypeLookups.labelById[numeric] ?? text;
      }
    }

    const matchedId = jobTypeLookups.idByCanon[jobTypeLookups.canonJobType(text)];
    return matchedId != null ? (jobTypeLookups.labelById[matchedId] ?? text) : text;
  }, [ jobTypeLookups ]);

  const download = async () => {
    if (isDownloading) return;
    setIsDownloading(true);
    try {
      // 1) get all staff (we only need teachers, but we’ll use classes to resolve names)
      const staff = await (repository as any).StaffRepository.getStaff();

      const classes: Array<{ id: number; name: string }> = staff?.classes ?? [];
      const teachers: Array<{
        id: number;
        firstName?: string;
        lastName?: string;
        email?: string | null;
        phone?: string | null;
        isAdmin?: number | boolean;
        jobType?: string | number | null;
        classesIds?: number[];
      }> = staff?.teachers ?? [];

      const teachersClasses: Array<{
        teacherId: number;
        classId: number;
        isSubscribedToWeeklyUpdates?: boolean | number | { type: 'Buffer'; data: number[] };
      }> = staff?.teachersClasses ?? [];

      // 2) build classId -> name lookup
      const classNameById = new Map<number, string>(
        classes.map((c) => [ Number(c.id), String(c.name ?? '').trim() ]),
      );

      const isSubscribed = (value: any): boolean => {
        if (!value) return false;

        if (value.type === 'Buffer') {
          return value.data?.[0] === 1;
        }

        return Boolean(value);
      };

      // 3) rows in the exact template order
      const rows = teachers.map((t) => {
        const classNames = (t.classesIds ?? [])
          .map((id) => classNameById.get(Number(id)) || '')
          .filter(Boolean)
          .join(',');

        const emailClassNames = teachersClasses
          .filter((tc) =>
            Number(tc.teacherId) === Number(t.id) &&
            isSubscribed(tc.isSubscribedToWeeklyUpdates),
          )
          .map((tc) => classNameById.get(Number(tc.classId)) || '')
          .filter(Boolean)
          .join(',');


        // `Title` (honorific) doesn’t exist on this endpoint; leave blank.
        // `Job Type` in the uploader is free text; default to "Teacher" when missing.
        const jobTypeText = localizeJobTypeText(t.jobType);

        return {
          [translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.TEACHERS.JOB_TYPE')]: jobTypeText,
          [translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.TEACHERS.FIRST_NAME')]: t.firstName ?? '',
          [translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.TEACHERS.LAST_NAME')]: t.lastName ?? '',
          [translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.TEACHERS.EMAIL')]: t.email ?? '',
          [translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.TEACHERS.RECOVERY_EMAIL')]: '',
          [translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.TEACHERS.PHONE')]: t.phone ?? '',
          [translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.TEACHERS.CLASSES')]: classNames,
          [translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.TEACHERS.EMAIL_CLASSES')]: emailClassNames,
          [translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.TEACHERS.ADMIN')]: t.isAdmin ? 'Y' : '',
        };
      });

      const getTeacherExportHeaders = (t: (k: string) => string) => ([
        t('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.TEACHERS.JOB_TYPE'),
        t('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.TEACHERS.FIRST_NAME'),
        t('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.TEACHERS.LAST_NAME'),
        t('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.TEACHERS.EMAIL'),
        t('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.TEACHERS.RECOVERY_EMAIL'),
        t('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.TEACHERS.PHONE'),
        t('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.TEACHERS.CLASSES'),
        t('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.TEACHERS.EMAIL_CLASSES'),
        t('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.TEACHERS.ADMIN'),
      ]);

      exportFromJson({
        data: rows,
        fileName: translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.COMMON.EXPORT_TEACHERS_FILENAME') || 'teachers_export',
        exportType: 'csv',
        withBOM: true,
        fields: getTeacherExportHeaders(translate),
      });
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <Button
      className="w-48"
      color="positive"
      isOutline
      icon={GLOBAL_ICONS.download}
      onClick={download}
      disabled={isDownloading}
    >
      {translate('pages.ROSTER_MANAGEMENT.UPLOAD.BUTTONS.EXISTING_STAFF')}
    </Button>
  );
};

const ExportParents = () => {
  const { translate } = useI18n();
  const [ isDownloading, setIsDownloading ] = React.useState(false);

  const cleanCsvHeader = (value: string) =>
    String(value ?? '')
      .replace(/<[^>]*>/g, '')
      .replace(/&nbsp;/g, ' ')
      .trim();

  const csvCell = (value: string | number | null | undefined) => {
    const text = String(value ?? '');
    return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
  };

  const downloadCsv = (fileName: string, rows: Array<Array<string | number | null | undefined>>) => {
    const csv = rows.map((row) => row.map(csvCell).join(',')).join('\r\n') + '\r\n';
    const blob = new Blob([ csv ], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName.endsWith('.csv') ? fileName : `${fileName}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const getParentExportHeaders = (t: (k: string) => string) => ([
    cleanCsvHeader(t('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.PARENTS.UPN')),
    cleanCsvHeader(t('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.PARENTS.STUDENT_NAME')),
    cleanCsvHeader(t('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.PARENTS.G1_NAME')),
    cleanCsvHeader(t('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.PARENTS.G1_EMAIL')),
    cleanCsvHeader(t('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.PARENTS.G2_NAME')),
    cleanCsvHeader(t('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.PARENTS.G2_EMAIL')),
    cleanCsvHeader(t('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.PARENTS.G3_NAME')),
    cleanCsvHeader(t('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.PARENTS.G3_EMAIL')),
  ]);

  const download = async () => {
    if (isDownloading) return;
    setIsDownloading(true);

    try {
      const students = await repository.StudentRepository.rosterManagementListItemsByAction(
        RosterManagementStudentActionEnum.guardians,
      );

      const rows = sortBy(students ?? [], [ 'lastName', 'firstName' ]).map((student: any) => {
        const guardians = Array.isArray(student.guardians) ? student.guardians.slice(0, 3) : [];

        return [
          student.upn ?? '',
          [ student.firstName, student.lastName ].filter(Boolean).join(' '),
          guardians[0]?.name ?? '',
          guardians[0]?.email ?? '',
          guardians[1]?.name ?? '',
          guardians[1]?.email ?? '',
          guardians[2]?.name ?? '',
          guardians[2]?.email ?? '',
        ];
      });

      downloadCsv(
        translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.COMMON.EXPORT_PARENTS_FILENAME') || 'parents_export',
        [ getParentExportHeaders(translate), ...rows ],
      );
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <Button
      className="w-48"
      color="positive"
      isOutline
      icon={GLOBAL_ICONS.download}
      onClick={download}
      disabled={isDownloading}
    >
      {translate('pages.ROSTER_MANAGEMENT.UPLOAD.BUTTONS.EXISTING_PARENTS')}
    </Button>
  );
};

export default Upload2;

